
import { supabase } from "../../supabaseClient";
import { ensureLessonBySlug } from "./lessons";
import {
  normalizeLevelKey,
  normalizeQuestKey,
} from "../sanity/sanityLevels";

const TABLE = "user_level_resume_progress";
const RESUME_PROMPT_SESSION_PREFIX = "gwin_resume_prompt_dismissed_";

// Temporary debugging. Change to false after troubleshooting.
const RESUME_DEBUG = true;

function debug(message, details = {}) {
  if (!RESUME_DEBUG) return;
  console.log(`[Gwin Resume] ${message}`, details);
}

function debugError(message, error, details = {}) {
  console.error(`[Gwin Resume] ${message}`, {
    ...details,
    error: error?.message || error,
    code: error?.code,
  });
}

function normalizeRoute({ questId, subquestId, levelKey }) {
  return {
    questId: String(normalizeQuestKey(questId) || ""),
    subquestId: String(subquestId || ""),
    levelKey: String(normalizeLevelKey(levelKey) || ""),
  };
}

async function getAuthenticatedUserId(explicitUserId) {
  if (explicitUserId) return explicitUserId;

  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) {
    debugError("Authentication lookup failed", error);
    throw error;
  }

  return user?.id || null;
}

async function resolveLessonId(levelKey) {
  const normalized = normalizeLevelKey(levelKey);

  if (!normalized) {
    debug("Cannot resolve lesson: missing level key");
    return null;
  }

  try {
    const lessonId = await ensureLessonBySlug(supabase, {
      levelKey: normalized,
    });

    debug("Resolved Supabase lesson", {
      levelKey: normalized,
      lessonId,
    });

    return lessonId;
  } catch (error) {
    debugError("Lesson resolution failed", error, {
      levelKey: normalized,
    });
    throw error;
  }
}

async function isLessonCompleted(userId, lessonId) {
  if (!userId || !lessonId) return false;

  const { data, error } = await supabase
    .from("lesson_progress")
    .select("completed")
    .eq("user_id", userId)
    .eq("lesson_id", lessonId)
    .maybeSingle();

  if (error) {
    debugError("Completion lookup failed", error, {
      lessonId,
    });
    throw error;
  }

  const completed = data?.completed === true;

  debug("Completion status", {
    lessonId,
    completed,
    progressRecordExists: !!data,
  });

  return completed;
}

async function removeRowsByIds(userId, ids) {
  const safeIds = [...new Set((ids || []).filter(Boolean))];

  if (!userId || safeIds.length === 0) return;

  debug("Deleting stale resume records", {
    count: safeIds.length,
    ids: safeIds,
  });

  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq("user_id", userId)
    .in("id", safeIds);

  if (error) {
    debugError("Resume deletion failed", error);
    throw error;
  }
}

export async function saveLevelResumeProgress({
  userId,
  questId,
  subquestId,
  levelKey,
  levelTitle = "",
  resumeSegment,
  resumeStepKey = null,
  resumeStepIndex = null,
  resumeStepType = null,
  finalQuizStartSegment = null,
  finalQuizStartStepKey = null,
  finalQuizStartStepIndex = null,
}) {
  debug("Save requested", {
    questId,
    subquestId,
    levelKey,
    resumeSegment,
    resumeStepKey,
    resumeStepIndex,
  });

  const uid = await getAuthenticatedUserId(userId);

  if (!uid || !resumeSegment) {
    debug("Save skipped: missing user or segment", {
      authenticated: !!uid,
      resumeSegment,
    });
    return null;
  }

  const route = normalizeRoute({
    questId,
    subquestId,
    levelKey,
  });

  if (!route.levelKey) {
    debug("Save skipped: missing level key");
    return null;
  }

  const lessonId = await resolveLessonId(route.levelKey);

  if (!lessonId) return null;

  const completed = await isLessonCompleted(uid, lessonId);

  if (completed) {
    debug("Save skipped: lesson already completed", {
      levelKey: route.levelKey,
      lessonId,
    });

    await deleteLevelResumeProgress({
      userId: uid,
      lessonId,
    });

    return null;
  }

  const payload = {
    user_id: uid,
    lesson_id: lessonId,

    // Retained for compatibility with existing React routes.
    quest_id: route.questId,
    subquest_id: route.subquestId,
    level_key: route.levelKey,

    level_title: String(levelTitle || "").trim() || null,
    resume_segment: String(resumeSegment),

    resume_step_key: resumeStepKey
      ? String(resumeStepKey)
      : null,

    resume_step_index: Number.isInteger(resumeStepIndex)
      ? resumeStepIndex
      : null,

    resume_step_type: resumeStepType
      ? String(resumeStepType)
      : null,

    final_quiz_start_segment: finalQuizStartSegment
      ? String(finalQuizStartSegment)
      : null,

    final_quiz_start_step_key: finalQuizStartStepKey
      ? String(finalQuizStartStepKey)
      : null,

    final_quiz_start_step_index:
      Number.isInteger(finalQuizStartStepIndex)
        ? finalQuizStartStepIndex
        : null,
  };

  debug("Writing resume record", {
    lessonId,
    levelKey: route.levelKey,
    resumeSegment: payload.resume_segment,
    resumeStepKey: payload.resume_step_key,
  });

  const { data, error } = await supabase
    .from(TABLE)
    .upsert(payload, {
      onConflict: "user_id,lesson_id",
    })
    .select("*")
    .single();

  if (error) {
    debugError("Resume save failed", error, {
      lessonId,
      levelKey: route.levelKey,
    });
    throw error;
  }

  debug("Resume saved successfully", {
    id: data.id,
    lessonId: data.lesson_id,
    levelKey: data.level_key,
    segment: data.resume_segment,
    stepKey: data.resume_step_key,
    updatedAt: data.updated_at,
  });

  return data;
}

export async function getLevelResumeProgress({
  userId,
  questId,
  subquestId,
  levelKey,
}) {
  debug("Resume lookup requested", {
    questId,
    subquestId,
    levelKey,
  });

  const uid = await getAuthenticatedUserId(userId);

  if (!uid) {
    debug("Resume lookup skipped: not authenticated");
    return null;
  }

  const route = normalizeRoute({
    questId,
    subquestId,
    levelKey,
  });

  if (!route.levelKey) {
    debug("Resume lookup skipped: missing level key");
    return null;
  }

  const lessonId = await resolveLessonId(route.levelKey);

  if (!lessonId) return null;

  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("user_id", uid)
    .eq("lesson_id", lessonId)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    debugError("Resume lookup failed", error, {
      lessonId,
      levelKey: route.levelKey,
    });
    throw error;
  }

  debug("Resume lookup result", {
    requestedLevelKey: route.levelKey,
    resolvedLessonId: lessonId,
    found: !!data,
    savedProgress: data
      ? {
          id: data.id,
          lessonId: data.lesson_id,
          levelKey: data.level_key,
          questId: data.quest_id,
          subquestId: data.subquest_id,
          segment: data.resume_segment,
          stepKey: data.resume_step_key,
          stepIndex: data.resume_step_index,
          updatedAt: data.updated_at,
        }
      : null,
  });

  if (!data) return null;

  const completed = await isLessonCompleted(uid, lessonId);

  if (completed) {
    debug("Resume discarded: lesson is completed", {
      lessonId,
      resumeId: data.id,
    });

    await removeRowsByIds(uid, [data.id]);
    return null;
  }

  debug("Returning saved resume position", {
    levelKey: data.level_key,
    segment: data.resume_segment,
    stepKey: data.resume_step_key,
  });

  return data;
}

export async function getLatestLevelResumeProgress({
  userId,
} = {}) {
  const uid = await getAuthenticatedUserId(userId);

  if (!uid) return null;

  const { data: rows, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("user_id", uid)
    .order("updated_at", { ascending: false })
    .limit(20);

  if (error) {
    debugError("Latest resume lookup failed", error);
    throw error;
  }

  debug("Latest resume records loaded", {
    count: rows?.length || 0,
    records: (rows || []).map((row) => ({
      levelKey: row.level_key,
      lessonId: row.lesson_id,
      segment: row.resume_segment,
    })),
  });

  if (!rows?.length) return null;

  const lessonIds = [
    ...new Set(
      rows.map((row) => row.lesson_id).filter(Boolean)
    ),
  ];

  if (lessonIds.length === 0) {
    return rows[0] || null;
  }

  const {
    data: completionRows,
    error: completionError,
  } = await supabase
    .from("lesson_progress")
    .select("lesson_id, completed")
    .eq("user_id", uid)
    .in("lesson_id", lessonIds);

  if (completionError) {
    debugError("Latest resume completion check failed", completionError);
    throw completionError;
  }

  const completedLessonIds = new Set(
    (completionRows || [])
      .filter((row) => row.completed === true)
      .map((row) => row.lesson_id)
  );

  const staleRowIds = rows
    .filter((row) =>
      completedLessonIds.has(row.lesson_id)
    )
    .map((row) => row.id);

  if (staleRowIds.length > 0) {
    await removeRowsByIds(uid, staleRowIds);
  }

  const latest =
    rows.find(
      (row) => !completedLessonIds.has(row.lesson_id)
    ) || null;

  debug("Latest eligible resume", {
    levelKey: latest?.level_key || null,
    segment: latest?.resume_segment || null,
  });

  return latest;
}

export async function deleteLevelResumeProgress({
  userId,
  lessonId,
  questId,
  subquestId,
  levelKey,
}) {
  const uid = await getAuthenticatedUserId(userId);

  if (!uid) return;

  let resolvedLessonId = lessonId;

  if (!resolvedLessonId && levelKey) {
    resolvedLessonId = await resolveLessonId(levelKey);
  }

  if (!resolvedLessonId) {
    debug("Delete skipped: unable to resolve lesson", {
      levelKey,
    });
    return;
  }

  debug("Deleting resume after completion", {
    lessonId: resolvedLessonId,
    levelKey,
  });

  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq("user_id", uid)
    .eq("lesson_id", resolvedLessonId);

  if (error) {
    debugError("Resume deletion failed", error, {
      lessonId: resolvedLessonId,
    });
    throw error;
  }

  debug("Resume deletion successful", {
    lessonId: resolvedLessonId,
  });
}

export function buildLevelResumePath(progress) {
  if (!progress) {
    debug("Resume path unavailable: missing progress");
    return "/dashboard";
  }

  const route = normalizeRoute({
    questId: progress.quest_id,
    subquestId: progress.subquest_id,
    levelKey: progress.level_key,
  });

  const segment = String(
    progress.resume_segment || "welcome"
  )
    .replace(/^\/+/, "")
    .trim();

  const path = `/play/${route.questId}/${route.subquestId}/${route.levelKey}/${
    segment || "welcome"
  }`;

  debug("Built resume path", {
    path,
    levelKey: route.levelKey,
    segment,
    stepKey: progress.resume_step_key,
  });

  return path;
}

export function getResumePromptDismissalKey(progressId) {
  return `${RESUME_PROMPT_SESSION_PREFIX}${progressId}`;
}

export function isResumePromptDismissed(progressId) {
  if (!progressId || typeof window === "undefined") {
    return false;
  }

  try {
    return (
      sessionStorage.getItem(
        getResumePromptDismissalKey(progressId)
      ) === "1"
    );
  } catch {
    return false;
  }
}

export function dismissResumePromptForSession(progressId) {
  if (!progressId || typeof window === "undefined") {
    return;

  }

  try {
    sessionStorage.setItem(
      getResumePromptDismissalKey(progressId),
      "1"
    );
  } catch {
    // Ignore browser storage restrictions.
  }
}

